import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Bell, Plus, Check, X, Trash2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const empty = { state: "", category: "", title: "", note: "", source_url: "", effective_date: "" };
const STATUS_STYLE = {
  pending_review: "bg-amber-100 text-amber-800",
  published: "bg-teal-100 text-teal-800",
  dismissed: "bg-slate-200 text-slate-500",
};

export default function AdminChanges() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tax, setTax] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/admin/compliance/changes").then((r) => setRows(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
    load();
  }, [user, loading, navigate, load]);

  if (loading || !user || user.role !== "admin") return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/admin/compliance/changes", form); toast.success("Change queued for review"); setForm(empty); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
    finally { setSaving(false); }
  };
  const act = async (id, action) => { try { await api.post(`/admin/compliance/changes/${id}/${action}`); toast.success(action === "publish" ? "Published live" : "Dismissed"); load(); } catch { toast.error("Action failed"); } };
  const del = async (id) => { await api.delete(`/admin/compliance/changes/${id}`); toast.success("Deleted"); load(); };

  const group = (s) => rows.filter((r) => r.status === s);

  const Card = ({ c }) => (
    <div data-testid={`change-row-${c.id}`} className="bg-white rounded-xl border border-blue-900/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-teal-700">{c.state_name}</span>
            <span className="text-xs text-slate-400">· {c.category_name}</span>
            <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[c.status]}`}>{c.status.replace("_", " ")}</span>
          </div>
          <p className="font-display font-semibold text-blue-950 mt-1.5">{c.title}</p>
          <p className="text-sm text-slate-500 mt-0.5">{c.note}</p>
          {c.effective_date && <p className="text-xs text-slate-400 mt-1">Effective/updated: {c.effective_date}</p>}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {c.status !== "published" && <Button size="sm" data-testid={`change-publish-${c.id}`} onClick={() => act(c.id, "publish")} className="bg-teal-600 hover:bg-teal-700 text-white gap-1 h-8"><Check className="w-4 h-4" /> Publish</Button>}
          {c.status === "pending_review" && <Button size="sm" variant="outline" data-testid={`change-dismiss-${c.id}`} onClick={() => act(c.id, "dismiss")} className="gap-1 h-8"><X className="w-4 h-4" /> Dismiss</Button>}
          <button onClick={() => del(c.id)} data-testid={`change-delete-${c.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg self-end"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-blue-800 mb-4 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> Admin</Link>
      <div className="flex items-center gap-3 mb-8">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-white"><Bell className="w-6 h-6" /></span>
        <div><h1 className="font-display text-3xl font-bold text-blue-950">Regulation Change Monitor</h1><p className="text-slate-500">Review changes before they publish to the public updates feed.</p></div>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        <form onSubmit={create} data-testid="change-form" className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm space-y-3 self-start">
          <h3 className="font-display font-semibold text-blue-950 flex items-center gap-2"><Plus className="w-4 h-4" /> Log a change</h3>
          {tax && <>
            <div><Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}><SelectTrigger data-testid="change-state" className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="max-h-72">{tax.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="change-category" className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="max-h-72">{tax.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Title</Label><Input data-testid="change-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" required /></div>
            <div><Label>Note</Label><Textarea data-testid="change-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
            <div><Label>Source URL</Label><Input data-testid="change-source" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="mt-1" placeholder="https://…" /></div>
            <div><Label>Effective / updated</Label><Input data-testid="change-effective" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="mt-1" placeholder="e.g. August 2026" /></div>
            <Button type="submit" disabled={saving} data-testid="change-save" className="w-full bg-blue-900 hover:bg-blue-950 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Queue for review"}</Button>
          </>}
        </form>

        <div className="space-y-8">
          <div>
            <h2 className="font-display text-lg font-bold text-blue-950 mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-amber-600" /> Pending review ({group("pending_review").length})</h2>
            <div className="space-y-3" data-testid="changes-pending">{group("pending_review").map((c) => <Card key={c.id} c={c} />)}{group("pending_review").length === 0 && <p className="text-sm text-slate-400">Nothing awaiting review.</p>}</div>
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-blue-950 mb-3">Published ({group("published").length})</h2>
            <div className="space-y-3" data-testid="changes-published">{group("published").map((c) => <Card key={c.id} c={c} />)}{group("published").length === 0 && <p className="text-sm text-slate-400">None published yet.</p>}</div>
          </div>
          {group("dismissed").length > 0 && (
            <div><h2 className="font-display text-lg font-bold text-blue-950 mb-3">Dismissed ({group("dismissed").length})</h2>
              <div className="space-y-3">{group("dismissed").map((c) => <Card key={c.id} c={c} />)}</div></div>
          )}
        </div>
      </div>
    </div>
  );
}
